import { useEffect, useRef } from "react";
import { AlertTriangle } from "lucide-react";

import { useServiceHealth } from "@/hooks/useServiceHealth";
import {
  shouldShowDegradedServiceBanner,
  type ServiceHealthFailureReason,
} from "@/lib/serviceHealth";

const messageForReason = (reason: ServiceHealthFailureReason | null): string => {
  if (reason === "offline") {
    return "Your device appears offline. Travel search, booking, and account updates will resume when your connection returns.";
  }

  return "Travel search, booking, and account services are temporarily degraded. We’re retrying automatically; pages you already loaded remain available.";
};

const BANNER_HEIGHT_CSS_VAR = "--degraded-service-banner-height";

const setBannerHeightCssVar = (heightPx: number): void => {
  if (typeof document === "undefined") return;
  document.documentElement.style.setProperty(
    BANNER_HEIGHT_CSS_VAR,
    `${Math.max(0, Math.ceil(heightPx))}px`
  );
};

export const DegradedServiceBanner = () => {
  const serviceHealth = useServiceHealth();
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const showBanner = shouldShowDegradedServiceBanner(serviceHealth);

  useEffect(() => {
    if (!showBanner) {
      setBannerHeightCssVar(0);
      return;
    }

    const banner = bannerRef.current;
    if (!banner) return;

    const updateHeight = () => {
      setBannerHeightCssVar(banner.getBoundingClientRect().height);
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(updateHeight);
    observer.observe(banner);
    return () => {
      observer.disconnect();
    };
  }, [showBanner]);

  if (!showBanner) return null;
  return (
    <div
      ref={bannerRef}
      role="status"
      aria-live="polite"
      className="border-y border-amber-200 bg-amber-50 px-4 py-3 text-amber-950"
    >
      <div className="mx-auto flex max-w-7xl items-start gap-3 text-sm font-medium leading-6">
        <AlertTriangle
          aria-hidden="true"
          className="mt-0.5 h-5 w-5 flex-none text-amber-600"
        />
        <p>{messageForReason(serviceHealth.reason)}</p>
      </div>
    </div>
  );
};
